import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatPrice, formatSecondsToHms } from "@utils/format";

import "./OnProcessCard.scss";
import {
  onProcessinvoiceType,
  onProcessItemType,
} from "@store/OnProcessPayment";
import dayjs from "dayjs";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";
import { AlertError } from "@utils/alert";
import ButtonLoader from "@components/ButtonLoader/ButtonLoader";
import RemoveIcon from "@mui/icons-material/Remove";
import { toJS } from "mobx";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

interface OnProcessCardProps extends onProcessItemType {
  openPdf: () => void;
  cancelProcess: () => void;
  fetchingDeleteOnProcess: string;
  time: number;
  onPressPayment: () => void;
}

const OnProcessCard = observer(
  ({
    paymentRef,
    description,
    invoices,
    openPdf,
    url,
    expiredAt,
    status,
    cancelProcess,
    fetchingDeleteOnProcess,
    time,
    id,
    paymentPlatform,
    onPressPayment,
  }: OnProcessCardProps) => {
    const [timer, setTimer] = useState<number | null>(null);

    useEffect(() => {
      setTimer(dayjs(expiredAt).unix() - time);
    }, [time]);

    useEffect(() => {
      const timerCount = setInterval(() => {
        if (status === "new" && timer && timer >= 0) {
          setTimer(timer - 1);
        }
      }, 1000);
      return () => clearInterval(timerCount);
    }, [status, timer]);

    const amountInvoiceCreditNote = useCallback(
      (invoice: onProcessinvoiceType) => {
        if (invoice === null) {
          return 0;
        }

        if (invoice.amount === null) {
          return 0;
        }

        if (
          invoice.appliedCreditNotes === null ||
          invoice.appliedCreditNotes.length === 0
        ) {
          return 0;
        }

        let amounCreditNote = 0;
        for (let i = 0; i < invoice.appliedCreditNotes.length; i++) {
          amounCreditNote += invoice.appliedCreditNotes[i]?.amount || 0;
        }

        return invoice.amount - amounCreditNote;
      },
      [invoices]
    );

    return (
      <div key={id} className={"w-100 onProcessCard__mainWrapper"}>
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-7/12 w-full">
            <p className="onProcessCard__ticketNo header--18">{`Payment - ${description}`}</p>
            <p className="onProcessCard__descriptionText id mb-10">{`ID: ${paymentRef}`}</p>
            <p className="onProcessCard__descriptionText">{description}</p>
          </div>
          {status === "new" && timer ? (
            <div className="lg:grid lg:w-5/12 w-full lg:justify-items-end">
              <p className="timer">{`Payment expired in: ${formatSecondsToHms(
                timer
              )}`}</p>
              <div className="flex flex-row space-x-4">
                {status === "new" && timer && timer < 86220 ? (
                  <h2
                    className="cursor-pointer self-center font-semibold lg:text-center text-left"
                    onClick={() => {
                      if (fetchingDeleteOnProcess !== "pending") {
                        cancelProcess();
                      }
                    }}
                  >
                    Cancel Payment
                    {fetchingDeleteOnProcess === "pending" ? (
                      <CircularProgress
                        style={{ marginLeft: 10 }}
                        size={16}
                      ></CircularProgress>
                    ) : null}
                  </h2>
                ) : null}

                <ButtonLoader
                  className="paymentLink"
                  onClick={() => {
                    if (url) {
                      window.location.href = url;
                    } else {
                      AlertError("Payment no Found", "No payment link found");
                    }
                  }}
                >
                  Continue Payment
                </ButtonLoader>
              </div>
            </div>
          ) : null}
          {status === "paid" ? (
            <div className="col-xl-3">
              <p className="paymentReceived">Processing...</p>
            </div>
          ) : null}
          {status === "expired" ? (
            <div className="col-xl-3">
              <p className="paymentExpired">Payment has been expired.</p>
            </div>
          ) : null}
        </div>
        <div>
          {invoices
            ? invoices.map((invoice) => {
                return (
                  <div key={invoice.id}>
                    <div className="line" />
                    <div className="row">
                      <div className="col-sm-9 row">
                        <div className="col-10">
                          <p className="onProcessCard__ticketNo header--18">
                            {`Invoice - ${invoice.description}`}
                          </p>

                          <p className="onProcessCard__descriptionText">
                            {invoice.description}
                          </p>
                        </div>
                      </div>
                      <div className="col-sm-3 row onProcessCard__wrapperTag">
                        {/* {invoice.projectCode ? (
                          <div className="col-8 onProcessCard__center">
                            <div className="onProcessCard__labelWrapper">
                              {projectCodeTag(invoice.projectCode)}
                            </div>
                          </div>
                        ) : (
                          <div className="col-8"></div>
                        )} */}
                        {/* {invoice.mbcRef ? (
                          <div className="paymentItem__center col-4">
                            <div
                              onClick={() => {
                                openPdf();
                              }}
                              className="btnTicketPDF"
                            />
                          </div>
                        ) : null} */}
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className={`col-8`}>
                        <div className="row">
                          <div className="col-sm-4">
                            {invoice.amount ? (
                              <>
                                <label className="lableAmount">
                                  {invoice.appliedCreditNotes &&
                                  invoice.appliedCreditNotes?.length > 0
                                    ? "Original Amount"
                                    : "Amount"}
                                </label>
                                <div
                                  className={`priceText ${
                                    invoice.appliedCreditNotes &&
                                    invoice.appliedCreditNotes?.length > 0
                                      ? "scribbleText"
                                      : null
                                  }`}
                                >
                                  {`${formatPrice(invoice.amount)}`}
                                </div>
                              </>
                            ) : null}
                          </div>
                          <div className="col-sm-4">
                            {invoice.appliedCreditNotes &&
                            invoice.appliedCreditNotes?.length > 0 ? (
                              <>
                                <label className="lableAmount">Amount</label>
                                <div className={`priceText`}>
                                  {formatPrice(
                                    amountInvoiceCreditNote(invoice)
                                  )}
                                </div>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    {invoice.appliedCreditNotes &&
                      invoice.appliedCreditNotes?.length > 0 &&
                      invoice.appliedCreditNotes.map((appliedCreditNote) => {
                        return (
                          <div key={appliedCreditNote.id}>
                            <div className="line" />
                            <div className="row">
                              <div className="col-sm-9 row">
                                <div
                                  style={{ flex: 0.1, justifyItems: "center" }}
                                >
                                  <RemoveIcon />
                                </div>
                                <div style={{ flex: 12 }}>
                                  <p className="onProcessCard__ticketNo header--18">
                                    {`Credit Note - ${appliedCreditNote.id}`}
                                  </p>

                                  <p className="onProcessCard__descriptionText">
                                    {appliedCreditNote.description}
                                  </p>

                                  <div className="row mt-2">
                                    <div className={`col-8`}>
                                      <div className="row">
                                        <div className="col-sm-4">
                                          {appliedCreditNote.amount ? (
                                            <>
                                              <label className="lableAmount">
                                                Amount
                                              </label>
                                              <div className={`priceText`}>
                                                {formatPrice(
                                                  appliedCreditNote.amount
                                                )}
                                              </div>
                                            </>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })
            : null}
        </div>
      </div>
    );
  }
);

export default memo(OnProcessCard);
